import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";

export type ComboBoxOption = {
  value: string;
  label: string;
  sublabel?: string;
  extra?: Record<string, string>;
  displayId?: string;
};

type SearchComboBoxProps = {
  id: string;
  options: ComboBoxOption[];
  value: string;
  onChange: (value: string, selectedOption?: ComboBoxOption) => void;
  disabled?: boolean;
  placeholder?: string;
  hasError?: boolean;
  isRequired?: boolean;
  ariaDescribedBy?: string;
  maxLength?: number;
  noMatchMessage?: string;
};

const MENU_MAX_HEIGHT = 200;
const MENU_OFFSET = 4;

export default function SearchComboBox({
  id,
  options,
  value,
  onChange,
  disabled = false,
  placeholder = "Search or type to enter...",
  hasError = false,
  isRequired = false,
  ariaDescribedBy,
  maxLength = 50,
  noMatchMessage = "No matches found",
}: SearchComboBoxProps) {
  const getDisplayText = useCallback(
    (val: string) => {
      if (!val) return "";
      const match = options.find((o) => o.value === val);
      return match ? `${match.label} (${match.displayId ?? match.value})` : val;
    },
    [options]
  );

  const [searchText, setSearchText] = useState(() => getDisplayText(value));
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    openUp: false,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isTypingRef = useRef(false);

  useEffect(() => {
    if (isTypingRef.current || isOpen) {
      isTypingRef.current = false;
      return;
    }
    setSearchText(getDisplayText(value));
  }, [value, getDisplayText, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp =
        spaceBelow < MENU_MAX_HEIGHT + MENU_OFFSET && rect.top > spaceBelow;
      setMenuPosition({
        top: openUp ? rect.top - MENU_OFFSET : rect.bottom + MENU_OFFSET,
        left: rect.left,
        width: rect.width,
        openUp,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        (!listRef.current || !listRef.current.contains(target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = options.filter((o) => {
    const search = searchText.toLowerCase().trim();
    if (!search) return true;
    return (
      o.label.toLowerCase().includes(search) ||
      o.value.toLowerCase().startsWith(search) ||
      (o.displayId?.toLowerCase().startsWith(search) ?? false)
    );
  });

  const handleSelect = (option: ComboBoxOption) => {
    setSearchText(`${option.label} (${option.displayId ?? option.value})`);
    isTypingRef.current = true;
    onChange(option.value, option);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);
    setIsOpen(true);
    setHighlightedIndex(-1);
    isTypingRef.current = true;

    if (!val.trim()) {
      onChange("");
      return;
    }

    onChange(val);
  };

  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
          handleSelect(filtered[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll(".dropdown-item");
      const item = items[highlightedIndex] as HTMLElement;
      if (item) {
        const list = listRef.current;
        const itemTop = item.offsetTop;
        const itemBottom = itemTop + item.offsetHeight;
        if (itemBottom > list.scrollTop + list.clientHeight) {
          list.scrollTop = itemBottom - list.clientHeight;
        } else if (itemTop < list.scrollTop) {
          list.scrollTop = itemTop;
        }
      }
    }
  }, [highlightedIndex]);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <input
        id={id}
        ref={inputRef}
        type="text"
        className={`form-control${hasError ? " is-invalid" : ""}`}
        value={searchText}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        maxLength={maxLength}
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls={`${id}-listbox`}
        aria-required={isRequired || undefined}
        aria-describedby={ariaDescribedBy}
      />

      {isOpen &&
        !disabled &&
        createPortal(
          <div
            id={`${id}-listbox`}
            ref={listRef}
            role="listbox"
            className="dropdown-menu show"
            onMouseDown={(e) => e.preventDefault()}
            style={{
              position: "fixed",
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              transform: menuPosition.openUp
                ? "translateY(-100%)"
                : undefined,
              maxHeight: MENU_MAX_HEIGHT,
              overflowY: "auto",
              zIndex: 1060,
              padding: 0,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
          >
            {filtered.length > 0 ? (
              filtered.map((option, index) => {
                const isHighlighted = highlightedIndex === index;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isHighlighted}
                    className="dropdown-item"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(option);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    style={{
                      whiteSpace: "normal",
                      padding: "0.375rem 0.75rem",
                      borderBottom:
                        index < filtered.length - 1
                          ? "1px solid #e9ecef"
                          : "none",
                      backgroundColor: isHighlighted ? "#003a5d" : undefined,
                      color: isHighlighted ? "#ffffff" : undefined,
                    }}
                  >
                    <div style={{ fontWeight: 500, fontSize: "0.9375rem" }}>
                      {option.label}
                    </div>
                    {option.sublabel && (
                      <small
                        style={{
                          fontSize: "0.8125rem",
                          color: isHighlighted ? "#ffffffcc" : "#6c757d",
                        }}
                      >
                        {option.sublabel}
                      </small>
                    )}
                  </button>
                );
              })
            ) : (
              <div
                className="dropdown-item disabled text-muted"
                style={{ padding: "0.375rem 0.75rem", fontSize: "0.9375rem" }}
              >
                {noMatchMessage}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}